import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Fingerprint, FlaskConical, MapPin, RotateCcw, ScanEye, FileSearch, Gavel } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Forensics — /cef/forensics/:serverId — UV sweep crime-scene investigation.
   A dim scene is drawn on canvas; your UV torch (pointer) reveals glints where
   trace evidence hides. Click a glint to bag it, then run each exhibit through
   the lab. Matches come back against NCDB records — two exhibits on one name
   swears out a warrant automatically. Scenes go cold after 30 minutes. */

const EV_COLORS = { print: "#7dd3fc", fiber: "#c084fc", casing: "#fbbf24", dna: "#f87171", tool: "#a3e635", glass: "#e2e8f0" };

const DEMO_SCENE = {
  id: "demo-scene", kind: "robbery", label: "Armed Robbery", location_label: "24/7 Fuel Dock",
  status: "fresh", fresh_until: new Date(Date.now() + 26 * 60000).toISOString(),
  evidence: [
    { id: "e1", type: "print", x: 0.3, y: 0.55, found: false, collected: false, analyzed: false },
    { id: "e2", type: "casing", x: 0.62, y: 0.72, found: false, collected: false, analyzed: false },
    { id: "e3", type: "glass", x: 0.78, y: 0.4, found: false, collected: false, analyzed: false },
    { id: "e4", type: "fiber", x: 0.45, y: 0.3, found: false, collected: false, analyzed: false },
  ],
};

export default function ForensicsCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const canvasRef = useRef(null);
  const [scene, setScene] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const pointer = useRef({ x: -1, y: -1 });

  const say = (msg) => setLog((l) => [{ at: new Date().toLocaleTimeString(), msg }, ...l].slice(0, 8));

  const [mystery, setMystery] = useState(null);
  const [accuse, setAccuse] = useState({ suspect: "", motive: "" });

  const load = async () => {
    if (demo) {
      setScenes([DEMO_SCENE]);
      setMystery({ id: "demo-m", title: "The Midnight Cargo Vanish", reward: 2500, status: "open",
        briefing: "A Ghost-Pak box truck arrived at the docks with its seals intact and its cargo gone. No forced entry. The driver swears the radio played static for exactly one red light.",
        suspects: [{ name: "Ray Dalton", motive: "" }, { name: "Ada King", motive: "" }, { name: "Mia Torres", motive: "" }],
        evidence: [{ label: "Scorched relay fuse", detail: "the tracker was looped, not cut", kind: "physical" }, { label: "Toll camera still", detail: "plates match, driver silhouette doesn't", kind: "visual" }] });
      return;
    }
    try {
      const r = await api.get(`/nc/servers/${serverId}/forensics/scenes`, { params: { token } });
      setScenes(r.data.scenes);
      const m = await api.get(`/nc/servers/${serverId}/mysteries/active`, { params: { token } });
      setMystery(m.data.active);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load scenes"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accuseSubmit = async () => {
    if (busy || !accuse.suspect || accuse.motive.trim().length < 3) return;
    setBusy(true);
    try {
      if (demo) { toast("Demo: accusations are disabled in preview — in-game the bounty is real"); setBusy(false); return; }
      const r = await api.post(`/nc/servers/${serverId}/mysteries/${mystery.id}/solve`, accuse, { params: { token } });
      r.data.solved ? toast.success(r.data.message) : toast.error(r.data.message);
      if (r.data.solved) setMystery(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Accusation rejected"); }
    finally { setBusy(false); }
  };

  const openScene = (s) => { setScene(JSON.parse(JSON.stringify(s))); setLog([]); say(`Cordon established — ${s.location_label}. UV torch out.`); };

  const spawnDemo = async () => {
    if (demo) { openScene(JSON.parse(JSON.stringify(DEMO_SCENE))); return; }
    try {
      const kinds = ["robbery", "homicide", "burglary", "hitrun"];
      const r = await api.post(`/nc/servers/${serverId}/forensics/scenes?token=${token}`, { kind: kinds[Math.floor(Math.random() * 4)] });
      toast.success("Scene spawned — dispatch logged");
      openScene(r.data.scene);
    } catch (e) { toast.error(e.response?.data?.detail || "Spawn failed"); }
  };

  /* UV sweep renderer */
  useEffect(() => {
    if (!scene) return;
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.clientWidth * dpr; canvas.height = canvas.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const frame = (now) => {
      const W = canvas.clientWidth, H = canvas.clientHeight;
      const t = now / 1000;
      ctx.clearRect(0, 0, W, H);
      // dark scene — barely visible until UV hits it
      ctx.fillStyle = "rgba(8,10,14,0.92)";
      ctx.beginPath(); ctx.roundRect(W * 0.03, H * 0.05, W * 0.94, H * 0.9, 14); ctx.fill();
      ctx.strokeStyle = "rgba(120,160,255,0.12)"; ctx.stroke();
      // floor boards + furniture silhouettes
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      for (let i = 1; i < 8; i++) { ctx.beginPath(); ctx.moveTo(W * 0.03, H * (0.05 + i * 0.11)); ctx.lineTo(W * 0.97, H * (0.05 + i * 0.11)); ctx.stroke(); }
      ctx.fillStyle = "rgba(255,255,255,0.03)";
      ctx.fillRect(W * 0.08, H * 0.12, W * 0.2, H * 0.28);
      ctx.fillRect(W * 0.66, H * 0.55, W * 0.26, H * 0.3);
      // UV torch pool
      const px = pointer.current.x * W, py = pointer.current.y * H;
      if (px > 0) {
        const g = ctx.createRadialGradient(px, py, 10, px, py, W * 0.16);
        g.addColorStop(0, "rgba(150,110,255,0.22)");
        g.addColorStop(1, "rgba(150,110,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(px, py, W * 0.16, 0, 6.2832); ctx.fill();
      }
      // evidence glints — only visible inside the torch pool
      for (const ev of scene.evidence) {
        if (ev.collected) continue;
        const ex = ev.x * W, ey = ev.y * H;
        const d = Math.hypot(ex - px, ey - py);
        const vis = Math.max(0, 1 - d / (W * 0.15));
        if (vis > 0.05) {
          const pulse = 0.6 + 0.4 * Math.sin(t * 4 + ev.x * 20);
          ctx.fillStyle = EV_COLORS[ev.type] || "#fff";
          ctx.globalAlpha = vis * pulse;
          ctx.beginPath(); ctx.arc(ex, ey, 4 + 2 * pulse, 0, 6.2832); ctx.fill();
          ctx.globalAlpha = vis * 0.35;
          ctx.beginPath(); ctx.arc(ex, ey, 10 + 4 * pulse, 0, 6.2832); ctx.fill();
          ctx.globalAlpha = 1;
          if (vis > 0.4 && !ev.found) {
            ev.found = true;
            say(`Trace found — ${ev.type.toUpperCase()} signature. Click the glint to bag it.`);
          }
        }
      }
      // bagged markers
      for (const ev of scene.evidence) {
        if (!ev.collected) continue;
        ctx.strokeStyle = "rgba(74,222,128,0.6)";
        ctx.strokeRect(ev.x * W - 7, ev.y * H - 7, 14, 14);
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", fit); document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, [scene]);

  const onMove = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    pointer.current = { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onClick = async () => {
    if (!scene || busy) return;
    const hit = scene.evidence.find((ev) => ev.found && !ev.collected && Math.hypot(ev.x - pointer.current.x, ev.y - pointer.current.y) < 0.045);
    if (!hit) return;
    setBusy(true);
    try {
      if (!demo) await api.post(`/nc/servers/${serverId}/forensics/scenes/${scene.id}/collect`, { token, evidence_id: hit.id });
      hit.collected = true;
      setScene({ ...scene });
      say(`${hit.type.toUpperCase()} bagged and tagged. Run it through the lab →`);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Collect failed"); }
    finally { setBusy(false); }
  };

  const analyze = async (ev) => {
    setBusy(true);
    try {
      if (demo) {
        ev.analyzed = true;
        const hit = Math.random() < 0.5;
        say(hit ? `LAB: ${ev.type.toUpperCase()} — NCDB HIT (demo)` : `LAB: ${ev.type.toUpperCase()} — no hit on file (demo)`);
      } else {
        const r = await api.post(`/nc/servers/${serverId}/forensics/scenes/${scene.id}/analyze`, { token, evidence_id: ev.id });
        ev.analyzed = true;
        say(`LAB: ${r.data.message}`);
        if (r.data.warrant_issued) toast.success("WARRANT ISSUED — CAD paged");
        else if (r.data.matched) toast.success("NCDB hit logged as a lead");
      }
      setScene({ ...scene });
    } catch (e2) { toast.error(e2.response?.data?.detail || "Analysis failed"); }
    finally { setBusy(false); }
  };

  const cold = scene && new Date(scene.fresh_until).getTime() < Date.now();

  return (
    <div data-testid="forensics-page" className="min-h-screen bg-transparent flex flex-col items-center justify-end pb-5 relative overflow-hidden select-none">
      {demo && <PreviewRibbon what="forensics kit" />}
      <div className="absolute top-4 inset-x-0 flex flex-col items-center gap-2 pointer-events-none z-10">
        <div className="flex items-center gap-2 bg-black/80 border border-white/15 px-3 py-2 backdrop-blur-md">
          <ScanEye className="w-3.5 h-3.5 text-violet-400" />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-300">
            {scene ? `${scene.label} — ${scene.location_label}${cold ? " · SCENE COLD (degraded)" : ""}` : "Forensics — pick a scene"}
          </span>
        </div>
      </div>

      {!scene ? (
        <div className="w-full max-w-lg border border-white/12 bg-black/88 backdrop-blur-md p-5 flex flex-col gap-3" style={{ animation: "ncFadeUp 0.3s ease both" }}>
          <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }`}</style>

          {/* weekly cold case — the whole city is a detective */}
          {mystery && (
            <div data-testid="mystery-board" className="border border-amber-500/30 bg-amber-500/[0.05] p-4 flex flex-col gap-2.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-amber-400 flex items-center gap-2">
                <FileSearch className="w-3.5 h-3.5" /> Cold case of the week · ${mystery.reward?.toLocaleString()} bounty
              </p>
              <p className="font-sans font-bold text-sm text-zinc-50 uppercase tracking-wide">{mystery.title}</p>
              <p className="font-mono text-[9px] text-zinc-400 leading-relaxed">{mystery.briefing}</p>
              <div className="flex flex-col gap-1">
                {(mystery.evidence || []).map((e, i) => (
                  <p key={i} data-testid="mystery-evidence" className="font-mono text-[8px] text-zinc-500 flex gap-1.5">
                    <span className="text-amber-500/70 uppercase tracking-[0.1em] shrink-0">[{e.kind}]</span> {e.label} — {e.detail}
                  </p>
                ))}
              </div>
              <div className="flex flex-col gap-2 border-t border-amber-500/20 pt-2.5">
                <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-500">name the culprit + the motive — one accusation per day</p>
                <div className="flex gap-1.5 flex-wrap">
                  {(mystery.suspects || []).map((s) => (
                    <button key={s.name} data-testid={`mystery-suspect-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setAccuse((a) => ({ ...a, suspect: s.name }))}
                      className={`font-mono text-[8px] uppercase tracking-[0.1em] px-2.5 py-1.5 border transition-colors ${accuse.suspect === s.name ? "border-amber-400/60 text-amber-300 bg-amber-400/10" : "border-white/15 text-zinc-400 hover:border-white/35"}`}>
                      {s.name}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <input data-testid="mystery-motive" value={accuse.motive} maxLength={200} onChange={(e) => setAccuse((a) => ({ ...a, motive: e.target.value }))}
                    placeholder="the motive, in a few words"
                    className="flex-1 bg-black/60 border border-white/15 font-mono text-[10px] text-zinc-100 px-2.5 py-2 [color-scheme:dark]" />
                  <button data-testid="mystery-accuse" onClick={accuseSubmit} disabled={busy || !accuse.suspect || accuse.motive.trim().length < 3}
                    className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-2 flex items-center gap-1.5 transition-colors">
                    <Gavel className="w-3 h-3" /> Accuse
                  </button>
                </div>
              </div>
            </div>
          )}

          {scenes.map((s) => (
            <button key={s.id} data-testid={`scene-${s.id}`} onClick={() => openScene(s)}
              className="flex items-center gap-3 border border-white/10 hover:border-violet-400/50 bg-black/40 px-4 py-3 text-left transition-colors">
              <MapPin className="w-4 h-4 text-violet-400 shrink-0" />
              <div>
                <p className="font-mono text-[11px] text-zinc-100 uppercase tracking-[0.1em]">{s.label} — {s.location_label}</p>
                <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">{s.evidence.length} potential exhibits · {s.status}</p>
              </div>
            </button>
          ))}
          <button data-testid="forensics-spawn" onClick={spawnDemo}
            className="mt-1 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] bg-violet-500 text-black">
            Open a fresh cordon
          </button>
        </div>
      ) : (
        <>
          <canvas data-testid="forensics-canvas" ref={canvasRef}
            className="w-full max-w-4xl h-[62vh] cursor-none touch-none"
            onPointerMove={onMove} onPointerDown={onClick} />
          <div className="w-full max-w-4xl flex gap-2 mt-2 items-stretch">
            <div className="flex-1 border border-white/12 bg-black/88 backdrop-blur-md p-3 max-h-32 overflow-y-auto">
              {log.map((l, i) => <p key={i} data-testid={`forensics-log-${i}`} className="font-mono text-[9px] text-zinc-400 leading-relaxed"><span className="text-zinc-600">{l.at}</span> — {l.msg}</p>)}
            </div>
            <div className="border border-white/12 bg-black/88 backdrop-blur-md p-3 flex flex-col gap-1.5 min-w-44">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5"><FlaskConical className="w-3 h-3" /> Lab queue</p>
              {scene.evidence.filter((e) => e.collected && !e.analyzed).map((e) => (
                <button key={e.id} data-testid={`analyze-${e.id}`} onClick={() => analyze(e)} disabled={busy}
                  className="font-mono text-[9px] uppercase tracking-[0.15em] border border-violet-400/40 text-violet-300 hover:bg-violet-400/10 px-2 py-1.5 text-left transition-colors disabled:opacity-40">
                  Analyze {e.type}
                </button>
              ))}
              {scene.evidence.filter((e) => e.collected && !e.analyzed).length === 0 && (
                <p className="font-mono text-[8px] text-zinc-600">nothing bagged</p>
              )}
              <button data-testid="forensics-exit" onClick={() => { setScene(null); load(); }}
                className="mt-auto font-mono text-[9px] uppercase tracking-[0.15em] border border-white/15 text-zinc-500 hover:text-zinc-200 px-2 py-1.5 flex items-center gap-1 transition-colors">
                <RotateCcw className="w-3 h-3" /> Scenes
              </button>
            </div>
          </div>
          <p data-testid="forensics-hint" className="mt-2 font-mono text-[8px] text-zinc-500 bg-black/70 px-3 py-1 border border-white/10 uppercase tracking-[0.2em]">
            <Fingerprint className="w-3 h-3 inline mr-1" /> sweep the UV torch — glints are trace evidence — click to bag
          </p>
        </>
      )}
    </div>
  );
}
